import { useEffect, useState } from "react";
import { api } from "../lib/api";
import type { WriteupView } from "../lib/types";

// The personalized write-up (Taylor's Addendum D), under the line "Written for you by AI
// from your results" with a thumbs up or down (D7.5).
//
// It is written in the background right after scoring, so someone who lands on their
// results within a few seconds of submitting gets "pending": the page shows a bar that fills
// over about 25 seconds (how long an Opus write-up takes), asks again every few seconds and
// fills itself in. If it never arrives (both attempts failed) a v1 page shows nothing here
// and the codex at its foot stands in; a v4 page, which has no codex, says so in one line
// (WriteupParts). "off" (no model configured) shows nothing on either.

/** How long a write-up usually takes, in seconds: the bar is about 80% full by then. */
const TYPICAL_SECONDS = 25;
/** After this, the bar holds near full and the copy says it is taking longer than usual. */
const LONG_WAIT_SECONDS = 90;
/** Stop asking after this long; the page then says to refresh. */
const GIVE_UP_SECONDS = 30 * 60;

/**
 * How long to wait before asking again, in milliseconds: quickly at first, every four seconds
 * while the bar is running, then every fifteen while the page stays open. At an event a few
 * hundred people submit within a minute and the write-ups come back a few dozen at a time,
 * so some take minutes; they can read the rest of the page while theirs lands.
 */
export function askAgainAfter(tries: number, elapsedSeconds: number): number | null {
  if (elapsedSeconds >= GIVE_UP_SECONDS) return null;
  const first = [1500, 2500, 4000][tries];
  if (first !== undefined) return first;
  return elapsedSeconds < LONG_WAIT_SECONDS ? 4000 : 15_000;
}

/** How full the bar is after `seconds`: fast at first, about 80% at 25 seconds, never past 90%. */
export const progressAt = (seconds: number): number => 90 * (1 - Math.exp(-seconds / (TYPICAL_SECONDS / 2.2)));

/** What the bar says it is doing, by seconds waited. */
const STEPS: [number, string][] = [
  [0, "Reading your answers"],
  [5, "Looking at how your gifts fit together"],
  [10, "Writing about what's in the way"],
  [16, "Choosing your next step"],
  [22, "Nearly there"],
];
export const stepAt = (seconds: number): string => STEPS.filter(([from]) => seconds >= from).at(-1)![1];

/**
 * The write-up as the page holds it: what the result came with, then whatever asking again
 * brings back while it is "pending". Shared by the v1 block below and the v4 page, which
 * spreads the parts across its sections (WriteupParts).
 */
export function useWriteup({
  takeId,
  writeup,
  resultKey,
  readOnly = false,
}: {
  takeId: string;
  writeup?: WriteupView | null;
  resultKey?: string | null;
  readOnly?: boolean;
}): { view: WriteupView | null | undefined; gaveUp: boolean } {
  const [view, setView] = useState<WriteupView | null | undefined>(writeup);
  const [gaveUp, setGaveUp] = useState(false);
  const status = view?.status;

  useEffect(() => setView(writeup), [writeup]);

  useEffect(() => {
    if (readOnly || status !== "pending") return;
    let live = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let tries = 0;
    const since = Date.now();
    const askAgain = () => {
      const wait = askAgainAfter(tries, (Date.now() - since) / 1000);
      if (wait === null) {
        setGaveUp(true);
        return;
      }
      tries += 1;
      timer = setTimeout(() => {
        api
          .getWriteup(takeId, resultKey)
          .then((next) => {
            if (!live) return;
            setView(next);
            if (next.status === "pending") askAgain();
          })
          // A failed ask (a blip in the connection) just asks again later.
          .catch(() => live && askAgain());
      }, wait);
    };
    askAgain();
    return () => {
      live = false;
      if (timer) clearTimeout(timer);
    };
  }, [takeId, resultKey, readOnly, status]);

  return { view, gaveUp };
}

/** The v1 results page's write-up: one block, every heading in the order it was written. */
export function PersonalWriteup({
  takeId,
  writeup,
  resultKey,
  readOnly = false,
}: {
  takeId: string;
  writeup?: WriteupView | null;
  resultKey?: string | null;
  /** The admin's read-only copy: no asking again, no thumbs (those are the taker's routes). */
  readOnly?: boolean;
}) {
  const { view, gaveUp } = useWriteup({ takeId, writeup, resultKey, readOnly });
  const status = view?.status;

  if (!view) return null;
  if (status === "pending") {
    return readOnly ? null : (
      <section className="mt-14 border-t border-line pt-6" aria-labelledby="writeup-label" data-testid="writeup">
        <h2 id="writeup-label" className="eyebrow text-accent-text">
          {view.label}
        </h2>
        <WritingProgress gaveUp={gaveUp} />
      </section>
    );
  }
  if (status !== "ready" || (view.sections.length === 0 && (view.preamble ?? []).length === 0)) return null;

  return (
    <section className="writeup-in mt-14 border-t border-line pt-6" aria-labelledby="writeup-label" data-testid="writeup">
      <h2 id="writeup-label" className="eyebrow text-accent-text">
        {view.label}
      </h2>
      <div className="mt-4 flex flex-col gap-6">
        {(view.preamble ?? []).length > 0 ? (
          <div>
            {view.preamble!.map((paragraph, i) => (
              <Paragraph key={i} text={paragraph} />
            ))}
          </div>
        ) : null}
        {view.sections.map((part) => (
          <div key={part.heading}>
            <h3 className="heading text-[23px] leading-tight">{part.heading}</h3>
            {part.paragraphs.map((paragraph, i) => (
              <Paragraph key={i} text={paragraph} />
            ))}
          </div>
        ))}
      </div>
      {readOnly ? null : <Thumbs takeId={takeId} resultKey={resultKey} vote={view.feedback ?? null} />}
    </section>
  );
}

/** The four domain colours in a row (Strategic, Relating, Executing, Influencing): the bars' fill. */
export const FOUR_COLOURS =
  "linear-gradient(90deg, var(--color-sun) 0 25%, var(--color-coral) 25% 50%, var(--color-leaf) 50% 75%, var(--color-teal) 75% 100%)";

/**
 * The bar shown while the write-up is being written. Timed from when it appears (not from when
 * the take was submitted: the visitor's clock may be off). It never claims to be done: it eases
 * towards 90% and the write-up replaces it when it arrives.
 */
export function WritingProgress({ gaveUp, readyNote }: { gaveUp: boolean; readyNote?: string }) {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    const since = Date.now();
    const tick = setInterval(() => setSeconds((Date.now() - since) / 1000), 500);
    return () => clearInterval(tick);
  }, []);
  const long = seconds >= LONG_WAIT_SECONDS;
  const pct = Math.round(progressAt(seconds));

  return (
    <div className="mt-3">
      <p className="heading text-[23px] leading-tight">Your personalized results are being written</p>
      <div
        className="mt-4 h-2.5 w-full overflow-hidden rounded-full bg-soft"
        role="progressbar"
        aria-label="Writing your personalized results"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
      >
        <div
          className="h-full rounded-full transition-[width] duration-500 ease-linear motion-reduce:transition-none"
          style={{ width: `${pct}%`, backgroundImage: FOUR_COLOURS }}
        />
      </div>
      <p role="status" className="mt-3 text-[15px] leading-snug text-muted">
        {gaveUp
          ? "This is taking much longer than usual. Refresh the page in a few minutes to see it."
          : long
            ? "This is taking longer than usual. It will appear here on its own, so keep reading and check back."
            : `${stepAt(seconds)}… This can take a minute or two.${readyNote ? ` ${readyNote}` : ""}`}
      </p>
    </div>
  );
}

/**
 * A paragraph as text, with the model's **bold** lead-ins ("**With Relators:**") shown bold
 * rather than as asterisks. Nothing else is interpreted: it is never HTML.
 */
export function Inline({ text }: { text: string }) {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return <>{parts.map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : part))}</>;
}

/**
 * One paragraph of the write-up. A line the model marked as a heading of its own ("### A
 * thought") is shown as a small heading, without the hashes, so nothing it wrote is hidden
 * and nothing shows as markup.
 */
export function Paragraph({ text }: { text: string }) {
  const sub = /^#{1,6}\s+(.+)$/.exec(text);
  if (sub) return <p className="mt-4 text-[17px] font-bold leading-snug">{sub[1]}</p>;
  return (
    <p className="mt-2 text-[17px] leading-snug">
      <Inline text={text} />
    </p>
  );
}

/** Thumbs up or down, so people can flag a miss (D7.5). */
export function Thumbs({ takeId, resultKey, vote }: { takeId: string; resultKey?: string | null; vote: "up" | "down" | null }) {
  const [chosen, setChosen] = useState(vote);
  const [failed, setFailed] = useState(false);

  const send = async (next: "up" | "down") => {
    const previous = chosen;
    setChosen(next);
    setFailed(false);
    try {
      await api.rateWriteup(takeId, next, resultKey);
    } catch {
      setChosen(previous);
      setFailed(true);
    }
  };

  return (
    <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-line pt-4">
      <p className="text-[15px] text-muted" id="writeup-vote-label">
        Does this sound like you?
      </p>
      <div className="flex gap-2" role="group" aria-labelledby="writeup-vote-label">
        <button
          type="button"
          className={`btn min-h-11 px-4 text-[14px] ${chosen === "up" ? "btn-primary" : "btn-secondary"}`}
          aria-pressed={chosen === "up"}
          onClick={() => void send("up")}
        >
          Yes
        </button>
        <button
          type="button"
          className={`btn min-h-11 px-4 text-[14px] ${chosen === "down" ? "btn-primary" : "btn-secondary"}`}
          aria-pressed={chosen === "down"}
          onClick={() => void send("down")}
        >
          Not really
        </button>
      </div>
      {chosen && !failed ? (
        <p role="status" className="text-[14px] text-muted">
          Thanks. Every one of these gets read.
        </p>
      ) : null}
      {failed ? (
        <p role="status" className="text-[14px] text-muted">
          That didn't save. Try again.
        </p>
      ) : null}
    </div>
  );
}
